import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import styles from './DonutChart.module.css';
import { DEVICE_STATE_COLOR } from '../../constants';

export default function DonutChart({ data = [], width, height }){
  const gRef = useRef(null);
  const cache = useRef(data);

  const radius = Math.min(width, height) / 2;

  const ORDER = Object.keys(DEVICE_STATE_COLOR);

  const pie = d3.pie()
    .value(d => +d.value)
    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status))
    .padAngle(0.02);

  const arc = d3.arc()
    .innerRadius(radius * 0.6)
    .outerRadius(radius * 0.7)
    .cornerRadius(50);

  const color = d3.scaleOrdinal()
    .domain(ORDER)
    .range(ORDER.map(k => DEVICE_STATE_COLOR[k]));

  useEffect(() => {
    const g = d3.select(gRef.current);

    let slicesG = g.select('g.slices');
    if (slicesG.empty()) slicesG = g.append('g').attr('class', 'slices');

    let emptyG = g.select('g.empty');
    if (emptyG.empty()) emptyG = g.append('g').attr('class', 'empty');

    const normalized = (data || []).map(d => ({ ...d, value: +d.value || 0 }));
    const currData = normalized.filter(d => d.value > 0);

    const currPie = pie(currData);
    const prevPie = pie((cache.current || []).map(d => ({ ...d, value: +d.value || 0 })).filter(d => d.value > 0));
    const prevByKey = new Map(prevPie.map(p => [p.data.status, p]));

    emptyG.selectAll('*').remove();

    const paths = slicesG
      .selectAll('path.arc')
      .data(currPie, d => d.data.status);

    paths.exit()
      .transition()
      .duration(800)
      .attrTween('d', function (d) {
        const i = d3.interpolate(d, { startAngle: d.endAngle, endAngle: d.endAngle });
        return t => arc(i(t));
      })
      .remove();

    const entered = paths.enter()
      .append('path')
      .attr('class', 'arc')
      .attr('fill', d => color(d.data.status))
      .each(function (d) {
        const prev = prevByKey.get(d.data.status);
        this._current = prev || { startAngle: d.startAngle, endAngle: d.startAngle };
      })
      .attr('d', function () { return arc(this._current); });

    entered.merge(paths)
      .transition()
      .duration(800)
      .attr('fill', d => color(d.data.status))
      .attrTween('d', function (d) {
        const i = d3.interpolate(this._current, d);
        this._current = i(0);
        return t => arc(i(t));
      });

    if (currPie.length === 0) {
      const emptyPie = d3.pie()
        .value(d => d.value)
        .sort(null)
        .padAngle(0.08)([{ value: 20 }, { value: 10 }, { value: 20 }, { value: 30 }, { value: 40 }]);

      emptyG.selectAll('path')
        .data(emptyPie)
        .join('path')
        .attr('d', arc)
        .attr('fill', 'rgba(0,0,0,0)')
        .attr('stroke', 'rgb(52, 53, 135)')
        .attr('stroke-width', 1)
        .attr('stroke-linecap', 'round')
        .attr('stroke-dasharray', '4,4');
    }

    cache.current = normalized;
  }, [data, width, height]);

  return (
    <svg width={width} height={height}>
      <g ref={gRef} transform={`translate(${width / 2} ${height / 2})`} />
    </svg>
  );
};